import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Mail, MessageSquare, RefreshCw, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_KEY = "mocktpAdminAuth";

const formatDateTime = (value) => {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

export default function AdminContactMessages() {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [authToken, setAuthToken] = useState(() => sessionStorage.getItem(AUTH_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const authHeaders = useMemo(
    () => (authToken ? { Authorization: `Basic ${authToken}` } : {}),
    [authToken]
  );

  const loadMessages = async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contact`, { headers: authHeaders });
      const sorted = [...data].sort((a, b) => {
        const aTime = new Date(a.created_at || 0).getTime() || 0;
        const bTime = new Date(b.created_at || 0).getTime() || 0;
        return bTime - aTime;
      });
      setMessages(sorted);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(AUTH_KEY);
        setAuthToken("");
        toast.error("Admin login required.");
      } else if (err?.response?.status === 503) {
        toast.error("Admin access is not configured on the backend.");
      } else {
        toast.error("Unable to load contact messages.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const login = (event) => {
    event.preventDefault();
    if (!credentials.username.trim() || !credentials.password) {
      toast.error("Enter the admin username and password.");
      return;
    }
    const token = btoa(`${credentials.username.trim()}:${credentials.password}`);
    sessionStorage.setItem(AUTH_KEY, token);
    setAuthToken(token);
    setCredentials({ username: "", password: "" });
  };

  const logout = () => {
    sessionStorage.removeItem(AUTH_KEY);
    setAuthToken("");
    setMessages([]);
  };

  if (!authToken) {
    return (
      <main className="min-h-screen bg-[#080C16] text-white flex items-center justify-center px-6">
        <form
          onSubmit={login}
          className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-6 shadow-2xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-xl bg-[#00DC82] text-[#080C16] flex items-center justify-center">
              <Lock size={22} />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight">Contact Admin</h1>
              <p className="text-sm text-slate-400">MockTP internal dashboard</p>
            </div>
          </div>
          <label className="block text-sm text-slate-300 mb-2" htmlFor="admin-username">
            Username
          </label>
          <input
            id="admin-username"
            value={credentials.username}
            onChange={(event) => setCredentials((current) => ({ ...current, username: event.target.value }))}
            className="mb-4 h-11 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 text-sm outline-none focus:border-[#00DC82]"
          />
          <label className="block text-sm text-slate-300 mb-2" htmlFor="admin-password">
            Password
          </label>
          <input
            id="admin-password"
            type="password"
            value={credentials.password}
            onChange={(event) => setCredentials((current) => ({ ...current, password: event.target.value }))}
            className="mb-6 h-11 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 text-sm outline-none focus:border-[#00DC82]"
          />
          <button className="h-11 w-full rounded-full bg-[#00DC82] text-[#080C16] font-semibold hover:bg-[#00B86B] transition-colors">
            Sign in
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#080C16] text-white px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00DC82]">
              MockTP Internal
            </p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
              Contact Messages
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              {messages.length} submitted messages.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadMessages}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-white/10 px-3 text-sm text-slate-200 hover:bg-white/5"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={logout}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-white/10 px-3 text-sm text-slate-200 hover:bg-white/5"
            >
              <XCircle size={16} />
              Sign out
            </button>
          </div>
        </header>

        <section className="py-5">
          <div className="rounded-xl border border-[#00DC82]/25 bg-[#00DC82]/10 p-4 sm:max-w-xs">
            <p className="text-xs text-[#9EF5CB]">Total contact messages</p>
            <p className="mt-1 text-2xl font-semibold">{messages.length}</p>
          </div>
        </section>

        <section className="space-y-3">
          {loading && <p className="text-sm text-slate-400">Loading contact messages...</p>}
          {!loading && messages.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/[0.05] text-[#00DC82]">
                <MessageSquare size={24} />
              </div>
              <h2 className="text-lg font-semibold">No contact messages yet</h2>
              <p className="mt-2 text-sm text-slate-400">
                Messages submitted through the public contact form will appear here.
              </p>
            </div>
          )}
          {messages.map((message) => (
            <article key={message.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold">{message.name || "Unnamed contact"}</h2>
                  <a
                    href={`mailto:${message.email}`}
                    className="mt-1 inline-flex items-center gap-2 text-sm text-[#9EF5CB] hover:text-[#00DC82]"
                  >
                    <Mail size={15} />
                    {message.email}
                  </a>
                </div>
                <p className="text-sm text-slate-500">
                  Submitted {formatDateTime(message.created_at)}
                </p>
              </div>
              <p className="mt-4 whitespace-pre-wrap rounded-xl border border-white/10 bg-[#080C16] p-4 text-sm leading-relaxed text-slate-200">
                {message.message}
              </p>
            </article>
          ))}
        </section>
      </div>
    </main>
  );
}
